import React, { useState, useEffect } from 'react';
import { CommentInput } from './CommentInput';
import { SetInputForm } from './SetInputForm';
import { SetDisplay } from './SetDisplay';
import { RestTimer } from './RestTimer';
import { ExerciseCompleteModal } from './ExerciseCompleteModal';
import { ExerciseSelectionModal } from './ExerciseSelectionModal';
import { activeWorkoutService } from '../services/activeWorkout';
import type { ProgramWorkout, WorkoutSet } from '../types';

interface WorkoutSessionProps {
  workout: ProgramWorkout;
  onComplete: (workoutData: {
    workout_id: string;
    exercises: Array<{
      exercise_id: string;
      sets: WorkoutSet[];
      notes?: string;
    }>;
    notes?: string;
    duration_minutes: number;
  }) => void;
  onCancel: () => void;
  startingExerciseIndex?: number;
}

interface ExerciseProgress {
  exercise_id: string;
  sets: WorkoutSet[];
  notes: string;
  isCompleted: boolean;
}

/**
 * WorkoutSession - Main component for conducting a workout
 * Provides step-by-step exercise execution with set tracking
 * Requirements: 5.3, 6.1, 6.2
 */
export const WorkoutSession: React.FC<WorkoutSessionProps> = ({
  workout,
  onComplete,
  onCancel,
  startingExerciseIndex = 0
}) => {
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(startingExerciseIndex);
  const [exerciseProgress, setExerciseProgress] = useState<ExerciseProgress[]>([]);
  const [workoutNotes, setWorkoutNotes] = useState('');
  const [startTime] = useState(new Date());
  const [isCompleting, setIsCompleting] = useState(false);
  const [showRestTimer, setShowRestTimer] = useState(false);
  const [restTimerData, setRestTimerData] = useState<{
    exerciseName: string;
    setNumber: number;
    restSeconds: number;
  } | null>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showExerciseSelection, setShowExerciseSelection] = useState(false);

  // Initialize exercise progress
  useEffect(() => {
    // Try to load from active workout first
    const activeWorkout = activeWorkoutService.getActiveWorkout();
    
    if (activeWorkout && activeWorkout.workout.id === workout.id) {
      // Resume from saved state
      const resumedProgress: ExerciseProgress[] = workout.exercises.map(exercise => {
        const savedSets = activeWorkout.sets[exercise.exercise.id] || [];
        const savedNotes = activeWorkout.notes[exercise.exercise.id] || '';
        const isCompleted = activeWorkout.completedExercises.includes(exercise.exercise.id);
        
        return {
          exercise_id: exercise.exercise.id,
          sets: savedSets,
          notes: savedNotes,
          isCompleted
        };
      });
      
      setExerciseProgress(resumedProgress);
      setCurrentExerciseIndex(activeWorkout.currentExerciseIndex);
      console.log('Resumed workout progress from storage');
    } else {
      // Start fresh
      const initialProgress: ExerciseProgress[] = workout.exercises.map(exercise => ({
        exercise_id: exercise.exercise.id,
        sets: [],
        notes: '',
        isCompleted: false
      }));
      setExerciseProgress(initialProgress);
    }
  }, [workout]);

  const currentExercise = workout.exercises[currentExerciseIndex];
  const currentProgress = exerciseProgress[currentExerciseIndex];
  const isLastExercise = currentExerciseIndex === workout.exercises.length - 1;
  const completedExercises = exerciseProgress.filter(p => p.isCompleted).length;

  const addSet = (setData: Omit<WorkoutSet, 'id' | 'exercise_id' | 'set_number' | 'completed_at'>) => {
    if (!currentProgress) return;

    const newSet: WorkoutSet = {
      id: `temp-${Date.now()}-${Math.random()}`,
      exercise_id: currentExercise.exercise.id,
      set_number: currentProgress.sets.length + 1,
      completed_at: new Date().toISOString(),
      ...setData
    };

    const updatedProgress = [...exerciseProgress];
    updatedProgress[currentExerciseIndex] = {
      ...currentProgress,
      sets: [...currentProgress.sets, newSet]
    };
    setExerciseProgress(updatedProgress);

    // Save to active workout service
    activeWorkoutService.addSet(currentExercise.exercise.id, newSet);

    // Check if all planned sets are completed
    const targetSets = currentExercise.target_sets || 0;
    const completedSets = newSet.set_number;
    
    if (targetSets > 0 && completedSets >= targetSets) {
      // All planned sets completed
      
      // Check if all exercises are completed
      const allExercisesCompleted = exerciseProgress.every((p, idx) => 
        idx === currentExerciseIndex ? true : p.isCompleted
      );
      
      // Only start rest timer if NOT all exercises are completed
      if (!allExercisesCompleted) {
        const restSeconds = currentExercise.rest_seconds;
        if (restSeconds && restSeconds > 0) {
          setRestTimerData({
            exerciseName: currentExercise.exercise.name_ru,
            setNumber: newSet.set_number,
            restSeconds: restSeconds
          });
          setShowRestTimer(true);
        }
      }
      
      // Show completion modal
      setShowCompleteModal(true);
    } else {
      // Not all sets completed yet - just start rest timer
      const restSeconds = currentExercise.rest_seconds;
      if (restSeconds && restSeconds > 0) {
        setRestTimerData({
          exerciseName: currentExercise.exercise.name_ru,
          setNumber: newSet.set_number,
          restSeconds: restSeconds
        });
        setShowRestTimer(true);
      }
    }
  };

  const removeSet = (setIndex: number) => {
    if (!currentProgress) return;

    const updatedProgress = [...exerciseProgress];
    const updatedSets = currentProgress.sets.filter((_, index) => index !== setIndex);
    
    // Renumber remaining sets
    const renumberedSets = updatedSets.map((set, index) => ({
      ...set,
      set_number: index + 1
    }));

    updatedProgress[currentExerciseIndex] = {
      ...currentProgress,
      sets: renumberedSets
    };
    setExerciseProgress(updatedProgress);

    // Save to active workout service
    activeWorkoutService.removeSet(currentExercise.exercise.id, setIndex);
  };

  const updateExerciseNotes = (notes: string) => {
    if (!currentProgress) return;

    const updatedProgress = [...exerciseProgress];
    updatedProgress[currentExerciseIndex] = {
      ...currentProgress,
      notes
    };
    setExerciseProgress(updatedProgress);

    // Save to active workout service
    activeWorkoutService.updateNotes(currentExercise.exercise.id, notes);
  };

  const completeCurrentExercise = () => {
    if (!currentProgress || currentProgress.sets.length === 0) {
      alert('Добавьте хотя бы один подход перед завершением упражнения');
      return;
    }

    const updatedProgress = [...exerciseProgress];
    updatedProgress[currentExerciseIndex] = {
      ...currentProgress,
      isCompleted: true
    };
    
    // Update state immediately
    setExerciseProgress(updatedProgress);

    // Save to active workout service
    activeWorkoutService.completeExercise(currentExercise.exercise.id);

    // Check if all exercises are completed using the updated progress
    const allCompleted = updatedProgress.every(p => p.isCompleted);
    
    // Use setTimeout to ensure state is updated before showing modal
    setTimeout(() => {
      if (allCompleted) {
        // All exercises done - show completion modal
        setShowCompleteModal(true);
      } else {
        // Show exercise selection modal to choose next exercise
        setShowExerciseSelection(true);
      }
    }, 50);
  };

  const goToPreviousExercise = () => {
    if (currentExerciseIndex > 0) {
      const prevIndex = currentExerciseIndex - 1;
      setCurrentExerciseIndex(prevIndex);
      activeWorkoutService.updateProgress({ currentExerciseIndex: prevIndex });
    }
  };

  const goToNextExercise = () => {
    if (currentExerciseIndex < workout.exercises.length - 1) {
      const nextIndex = currentExerciseIndex + 1;
      setCurrentExerciseIndex(nextIndex);
      activeWorkoutService.updateProgress({ currentExerciseIndex: nextIndex });
    }
  };

  const handleRestTimerComplete = () => {
    setShowRestTimer(false);
    setRestTimerData(null);
  };

  const handleRestTimerSkip = () => {
    setShowRestTimer(false);
    setRestTimerData(null);
  };

  const handleRestTimerCancel = () => {
    setShowRestTimer(false);
    setRestTimerData(null);
  };

  const handleCompleteModalConfirm = () => {
    setShowCompleteModal(false);
    
    // Mark current exercise as completed if not already
    let updatedProgress = [...exerciseProgress];
    if (!currentProgress?.isCompleted) {
      updatedProgress[currentExerciseIndex] = {
        ...currentProgress!,
        isCompleted: true
      };
      setExerciseProgress(updatedProgress);
      activeWorkoutService.completeExercise(currentExercise.exercise.id);
    }
    
    // Check if all exercises are completed using the updated progress
    const allCompleted = updatedProgress.every(p => p.isCompleted);
    
    // Use setTimeout to ensure state is updated
    setTimeout(() => {
      if (allCompleted) {
        // All done - complete workout
        completeWorkout(true);
      } else {
        // Not all done - show exercise selection
        setShowExerciseSelection(true);
      }
    }, 50);
  };

  const handleCompleteModalCancel = () => {
    setShowCompleteModal(false);
    // User wants to add more sets, just close the modal
  };

  const handleSelectNextExercise = (exerciseIndex: number) => {
    setShowExerciseSelection(false);
    setCurrentExerciseIndex(exerciseIndex);
    activeWorkoutService.updateProgress({ currentExerciseIndex: exerciseIndex });
  };

  const handleFinishWorkoutFromSelection = () => {
    setShowExerciseSelection(false);
    completeWorkout(false);
  };

  const formatDuration = () => {
    const now = new Date();
    const durationMs = now.getTime() - startTime.getTime();
    const minutes = Math.floor(durationMs / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours > 0) {
      return `${hours}ч ${remainingMinutes}м`;
    }
    return `${minutes}м`;
  };

  const completeWorkout = async (skipIncompleteCheck = false) => {
    // Mark current exercise as completed if we're skipping the check
    // (called from completion modal after all sets are done)
    if (skipIncompleteCheck && !currentProgress?.isCompleted) {
      const updatedProgress = [...exerciseProgress];
      updatedProgress[currentExerciseIndex] = {
        ...currentProgress!,
        isCompleted: true
      };
      setExerciseProgress(updatedProgress);
      activeWorkoutService.completeExercise(currentExercise.exercise.id);
      
      // Use the updated progress for the workout data
      const incompletedExercises = updatedProgress.filter(p => !p.isCompleted);
      if (incompletedExercises.length === 0) {
        // All exercises completed, proceed without confirmation
        await saveWorkout(updatedProgress);
        return;
      }
    }
    
    const incompletedExercises = exerciseProgress.filter(p => !p.isCompleted);
    if (incompletedExercises.length > 0) {
      const confirmComplete = confirm(
        `У вас есть ${incompletedExercises.length} незавершённых упражнений. Завершить тренировку?`
      );
      if (!confirmComplete) return;
    }

    await saveWorkout(exerciseProgress);
  };

  const saveWorkout = async (progress: ExerciseProgress[]) => {
    setIsCompleting(true);
    
    try {
      console.log('Starting workout save process...');
      const endTime = new Date();
      const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));

      const workoutData = {
        workout_id: workout.id,
        exercises: progress.map(p => ({
          exercise_id: p.exercise_id,
          sets: p.sets,
          notes: p.notes || undefined
        })),
        notes: workoutNotes || undefined,
        duration_minutes: durationMinutes
      };

      console.log('Workout data prepared:', workoutData);
      await onComplete(workoutData);
      console.log('Workout saved successfully');
    } catch (error) {
      console.error('Error completing workout:', error);
      alert('Ошибка при сохранении тренировки');
    } finally {
      setIsCompleting(false);
    }
  };

  if (!currentExercise || !currentProgress) {
    return (
      <div className="workout-session p-4">
        <div className="text-center">
          <p className="text-gray-500">Загрузка упражнения...</p>
        </div>
      </div>
    );
  }

  // Show completion screen if all exercises are done
  const allExercisesCompleted = exerciseProgress.every(p => p.isCompleted);
  if (allExercisesCompleted) {
    return (
      <div className="workout-session p-4 max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-green-600 mb-2">
            Тренировка завершена!
          </h2>
          <p className="text-gray-600">
            Отличная работа! Время тренировки: {formatDuration()}
          </p>
        </div>

        {/* Workout Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold mb-3">Сводка тренировки:</h3>
          <div className="space-y-2">
            {exerciseProgress.map((progress, index) => {
              const exercise = workout.exercises[index];
              const totalSets = progress.sets.length;
              
              // Calculate summary based on exercise type
              const exerciseType = exercise.exercise.exercise_type || 'вес_повторения';
              let summary = '';
              
              switch (exerciseType) {
                case 'вес_повторения':
                  const totalReps = progress.sets.reduce((sum, set) => sum + (set.reps || 0), 0);
                  const maxWeight = Math.max(...progress.sets.map(set => set.weight || 0), 0);
                  summary = `${totalSets} подходов, ${totalReps} повторов`;
                  if (maxWeight > 0) summary += `, макс. ${maxWeight} кг`;
                  break;
                  
                case 'собственный_вес_повторения':
                  const bodyweightReps = progress.sets.reduce((sum, set) => sum + (set.reps || 0), 0);
                  summary = `${totalSets} подходов, ${bodyweightReps} повторов`;
                  break;
                  
                case 'на_время':
                  const totalTime = progress.sets.reduce((sum, set) => sum + (set.duration_seconds || 0), 0);
                  const mins = Math.floor(totalTime / 60);
                  const secs = totalTime % 60;
                  summary = `${totalSets} подходов, ${mins > 0 ? `${mins}м ` : ''}${secs}с`;
                  break;
                  
                case 'расстояние_время':
                  const totalDistance = progress.sets.reduce((sum, set) => sum + (set.distance_meters || 0), 0);
                  const totalDuration = progress.sets.reduce((sum, set) => sum + (set.duration_seconds || 0), 0);
                  summary = `${totalSets} подходов, ${totalDistance}м за ${Math.floor(totalDuration / 60)}:${(totalDuration % 60).toString().padStart(2, '0')}`;
                  break;
                  
                default:
                  summary = `${totalSets} подходов`;
              }
              
              return (
                <div key={progress.exercise_id} className="flex justify-between text-sm">
                  <span>{exercise.exercise.name_ru}</span>
                  <span className="text-gray-600">{summary}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Workout Notes */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Заметки о тренировке (необязательно)
          </label>
          <CommentInput
            value={workoutNotes}
            onChange={setWorkoutNotes}
            placeholder="Как прошла тренировка? Что заметили?"
            type="workout"
            context={{
              workoutDuration: Math.round((new Date().getTime() - startTime.getTime()) / (1000 * 60)),
              previousComments: exerciseProgress
                .filter(p => p.notes)
                .map(p => p.notes)
            }}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={() => completeWorkout(false)}
            disabled={isCompleting}
            className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCompleting ? 'Сохранение...' : 'Сохранить тренировку'}
          </button>
          <button
            onClick={onCancel}
            disabled={isCompleting}
            className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="workout-session p-4 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold">{workout.name}</h1>
          <p className="text-sm text-gray-600">
            Упражнение {currentExerciseIndex + 1} из {workout.exercises.length} • {formatDuration()}
          </p>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 p-2"
          title="Отменить тренировку"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Прогресс</span>
          <span>{completedExercises} / {workout.exercises.length}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(completedExercises / workout.exercises.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Current Exercise */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h2 className="text-lg font-semibold mb-1">
              {currentExercise.exercise.name_ru}
            </h2>
            <p className="text-sm text-gray-600 mb-2">
              {(currentExercise.exercise as any).muscle_group || currentExercise.exercise.primary_muscles?.join(', ')}
              {((currentExercise.exercise as any).equipment || currentExercise.exercise.equipment?.[0]) && ` • ${(currentExercise.exercise as any).equipment || currentExercise.exercise.equipment?.[0]}`}
            </p>
            
            {/* Target Sets/Reps and Rest Time */}
            <div className="text-sm text-blue-600 mb-3">
              {(currentExercise.target_sets || currentExercise.target_reps_min || currentExercise.target_reps_max) && (
                <div>
                  <span className="font-medium">Цель:</span>
                  {currentExercise.target_sets && <span className="ml-1">{currentExercise.target_sets} подходов</span>}
                  {(currentExercise.target_reps_min || currentExercise.target_reps_max) && (
                    <span className="ml-1">
                      × {currentExercise.target_reps_min || '?'}-{currentExercise.target_reps_max || '?'} повторов
                    </span>
                  )}
                </div>
              )}
              {currentExercise.rest_seconds && (
                <div>
                  <span className="font-medium">Отдых:</span>
                  <span className="ml-1">
                    {Math.floor(currentExercise.rest_seconds / 60)}:{(currentExercise.rest_seconds % 60).toString().padStart(2, '0')}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {currentProgress.isCompleted && (
            <div className="text-green-600 font-medium text-sm">
              ✓ Завершено
            </div>
          )}
        </div>

        {/* Instructions */}
        {(currentExercise.exercise as any).instructions && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Техника:</span> {(currentExercise.exercise as any).instructions}
            </p>
          </div>
        )}
      </div>

      {/* Sets List */}
      <div className="mb-6">
        <h3 className="font-medium mb-3">Подходы ({currentProgress.sets.length})</h3>
        
        {currentProgress.sets.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">💪</div>
            <p>Добавьте первый подход</p>
          </div>
        ) : (
          <div className="space-y-2 mb-4">
            {currentProgress.sets.map((set, index) => (
              <SetDisplay
                key={set.id}
                set={set}
                exercise={currentExercise.exercise}
                onRemove={() => removeSet(index)}
                showRemoveButton={true}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Set Form */}
      <SetInputForm 
        onAddSet={addSet} 
        currentExercise={currentExercise}
        currentSetNumber={currentProgress.sets.length + 1}
        previousSet={currentProgress.sets[currentProgress.sets.length - 1]}
      />

      {/* Exercise Notes */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Заметки к упражнению
        </label>
        <CommentInput
          value={currentProgress.notes}
          onChange={updateExerciseNotes}
          placeholder="Как выполнялось упражнение?"
          type="exercise"
          context={{
            exerciseType: currentExercise.exercise.name_ru,
            previousComments: exerciseProgress
              .filter(p => p.isCompleted && p.notes)
              .map(p => p.notes)
          }}
        />
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={goToPreviousExercise}
          disabled={currentExerciseIndex === 0}
          className="flex items-center px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Назад
        </button>

        <div className="flex space-x-3">
          {!currentProgress.isCompleted && (
            <button
              onClick={completeCurrentExercise}
              className="bg-green-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-green-700"
            >
              Завершить упражнение
            </button>
          )}
          
          {!isLastExercise && (
            <button
              onClick={goToNextExercise}
              className="flex items-center px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50"
            >
              Далее
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Rest Timer Modal */}
      {showRestTimer && restTimerData && (
        <RestTimer
          restSeconds={restTimerData.restSeconds}
          exerciseName={restTimerData.exerciseName}
          setNumber={restTimerData.setNumber}
          onComplete={handleRestTimerComplete}
          onSkip={handleRestTimerSkip}
          onCancel={handleRestTimerCancel}
        />
      )}

      {/* Exercise Complete Modal */}
      {showCompleteModal && (
        <ExerciseCompleteModal
          isLastExercise={exerciseProgress.every(p => p.isCompleted)}
          exerciseName={currentExercise.exercise.name_ru}
          onConfirm={handleCompleteModalConfirm}
          onCancel={handleCompleteModalCancel}
        />
      )}

      {/* Exercise Selection Modal */}
      {showExerciseSelection && (
        <ExerciseSelectionModal
          workout={workout}
          exerciseProgress={exerciseProgress}
          onSelectExercise={handleSelectNextExercise}
          onFinishWorkout={handleFinishWorkoutFromSelection}
        />
      )}
    </div>
  );
};

export default WorkoutSession;